/**
 * HesaEntryProfile — HESA-backed entry-requirements block for
 * DegreeDetail Section 2, used when the editorial `entry_requirements`
 * dictionary is missing (all 27,521 HESA-verified UK courses).
 *
 * Sources — per HESA Discover Uni 2025/26:
 *   - `typical_a_level`          e.g. "A*A*A"
 *   - `typical_tariff_points`    e.g. 176 (UCAS tariff)
 *   - `tariff_distribution`      histogram of T-bands showing what
 *                                enrolled students actually held
 *   - `entry_qualifications`     %-split by qual type at admission
 *
 * We also compute IB / IB-total via the existing `equivalentsFor` mapper
 * so international applicants see a rough IB-equivalent right on the
 * card. Everything renders inside the same visual language as the
 * existing per-curriculum cards (Card + label-uppercase + Ev evidence).
 */
import { Card } from "@/components/ui/card";
import { Ev } from "@/components/app/degree/DegreeDetailBits";
import { equivalentsFor } from "@/lib/gradeEquivalency";

const QUAL_LABEL = {
  a_level:       "A-level",
  baccalaureate: "IB / Baccalaureate",
  access:        "Access to HE",
  degree:        "Previous degree",
  foundation:    "Foundation year",
  no_quals:      "No formal quals",
  other:         "Other UK quals",
  other_he:      "Other HE",
};

// Map T-band token (T001, T048, …, T240) → readable UCAS-points label.
function tariffBandLabel(token) {
  const n = parseInt(token.replace(/^T0*/, ""), 10);
  if (!Number.isFinite(n)) return token;
  if (n <= 1) return "< 1";
  return `${n}-${n + 15}`;
}

export function HesaEntryProfile({ hesa, universityName, editorialALevel }) {
  if (!hesa) return null;
  // Prefer the editorial entry_requirements.a_level.typical when the
  // degree has one — HESA's cohort median mixes Access / foreign /
  // mature entrants and can badly under-report elite offers. Editorial
  // is our verified source of truth and must win the display.
  const aLevel = editorialALevel || hesa.typical_a_level;
  const tariff = hesa.typical_tariff_points;
  const dist   = hesa.tariff_distribution || null;
  const quals  = hesa.entry_qualifications || null;
  if (!aLevel && !tariff && !dist && !quals) return null;

  // IB equivalent via the shared anchor table (works from A-level → IB).
  let ibTypical = null;
  if (aLevel) {
    const eq = equivalentsFor("a_level", aLevel);
    if (eq?.equivalents?.ib != null) ibTypical = eq.equivalents.ib;
  }

  // Distribution histogram — sort by band asc and skip zero-count bands
  // so the chart is visually tight.
  const distRows = dist
    ? Object.entries(dist)
        .filter(([_, pct]) => (pct || 0) > 0)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([token, pct]) => ({ token, label: tariffBandLabel(token), pct }))
    : [];
  const maxPct = Math.max(...distRows.map((r) => r.pct), 1);

  // Entry-qualifications rows — drop zeros and sample_size row.
  const qualRows = quals
    ? Object.entries(quals)
        .filter(([k, v]) => k !== "sample_size" && (v || 0) > 0)
        .sort(([, a], [, b]) => (b - a))
        .map(([k, v]) => ({ code: k, label: QUAL_LABEL[k] || k, pct: v }))
    : [];

  const evidence = {
    source:       "HESA Discover Uni · Entry Profile (Discover Uni 2025/26)",
    confidence:   "verified",
    url:          "https://discoveruni.gov.uk/",
    date_checked: hesa.phase3_enriched_at,
  };

  return (
    <Card className="p-4 border-emerald-300 bg-emerald-50/40 mb-3" data-testid="hesa-entry-profile">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-underground-sc text-xs tracking-wider text-emerald-900">
          Typical offer (HESA entry profile)
        </div>
        {hesa.tariff_sample_size ? (
          <div className="text-[10px] font-mono text-stone-500">
            n = {hesa.tariff_sample_size} enrolled students
          </div>
        ) : null}
      </div>

      {/* Headline: A-level + UCAS + IB equivalent */}
      <div className="mt-3 grid md:grid-cols-3 gap-4 text-sm">
        {aLevel && (
          <div data-testid="hesa-entry-alevel">
            <div className="label-uppercase !text-[10px] text-stone-500">A-level typical</div>
            <div className="font-chip text-2xl font-semibold text-stone-900 mt-1">{aLevel}</div>
          </div>
        )}
        {tariff && (
          <div data-testid="hesa-entry-ucas">
            <div className="label-uppercase !text-[10px] text-stone-500">UCAS tariff</div>
            <div className="font-chip text-2xl font-semibold text-stone-900 mt-1">
              {tariff}
              <span className="text-sm font-normal text-stone-500 ml-1">pts</span>
            </div>
          </div>
        )}
        {ibTypical != null && (
          <div data-testid="hesa-entry-ib">
            <div className="label-uppercase !text-[10px] text-stone-500">IB equivalent (approx)</div>
            <div className="font-chip text-2xl font-semibold text-stone-900 mt-1">{ibTypical}</div>
          </div>
        )}
      </div>

      {/* Distribution histogram of tariff points */}
      {distRows.length > 0 && (
        <div className="mt-5" data-testid="hesa-entry-tariff-dist">
          <div className="label-uppercase !text-[10px] text-stone-500 mb-2">
            Where enrolled students actually scored
          </div>
          <div className="space-y-1">
            {distRows.map((r) => {
              const pct = (r.pct / maxPct) * 100;
              return (
                <div
                  key={r.token}
                  data-testid={`tariff-band-${r.token}`}
                  className="grid grid-cols-[5rem_1fr_2.5rem] items-center gap-2 text-[12px]"
                >
                  <div className="text-stone-600 font-mono text-right">
                    {r.label}
                  </div>
                  <div className="relative h-4 bg-stone-100 rounded-sm overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-emerald-600/70 rounded-sm"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-right font-mono tabular-nums text-stone-800">
                    {Math.round(r.pct)}%
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] font-mono text-stone-500 mt-1.5">
            Tariff bands (16-point UCAS buckets) · not a required offer
          </div>
        </div>
      )}

      {/* Entry-qualifications donut → simple stacked row */}
      {qualRows.length > 0 && (
        <div className="mt-5" data-testid="hesa-entry-qual-dist">
          <div className="label-uppercase !text-[10px] text-stone-500 mb-2">
            What qualifications this cohort held
            {quals?.sample_size ? <span className="normal-case ml-1 text-stone-400">· n = {quals.sample_size}</span> : null}
          </div>
          <div className="flex h-6 rounded-md overflow-hidden border border-emerald-200 bg-white" data-testid="qual-stack">
            {qualRows.map((r, i) => (
              <div
                key={r.code}
                data-testid={`qual-seg-${r.code}`}
                title={`${r.label} · ${r.pct}%`}
                className={`h-full ${
                  ["bg-emerald-600","bg-emerald-500","bg-emerald-400",
                   "bg-amber-500","bg-stone-400","bg-stone-500",
                   "bg-stone-600","bg-stone-700"][i % 8]
                }`}
                style={{ width: `${r.pct}%` }}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            {qualRows.map((r, i) => (
              <div key={r.code} className="inline-flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-sm ${
                  ["bg-emerald-600","bg-emerald-500","bg-emerald-400",
                   "bg-amber-500","bg-stone-400","bg-stone-500",
                   "bg-stone-600","bg-stone-700"][i % 8]
                }`} />
                <span className="text-stone-700">{r.label}</span>
                <span className="font-mono text-stone-500">{r.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 text-[11px] text-stone-600">
        This entry profile is HESA's snapshot of students who <b>actually enrolled</b>{universityName ? ` at ${universityName}` : ""} —
        not a guaranteed offer. Check the university's current course
        page for the official offer conditions.
      </div>
      <Ev ev={evidence} />
    </Card>
  );
}

export default HesaEntryProfile;
